import React, { useState } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChatConfig, ChatMessage, ChatState } from "@/components/landing2/chat/types";
import { GitHubIcon } from "@/components/landing2/chat/constants";
import { useChatFlow } from "@/components/landing2/chat/useChatFlow";
import { useStickToBottom } from "use-stick-to-bottom";
import { ChevronDown, RotateCcw, Send } from "lucide-react";
import { Icon } from "@/components/landing/Navbar";
import { formatMarkdown } from "@/components/formatMarkdown";

const DEFAULT_REPLIES: ChatConfig["replies"] = [
  {
    text: "got it — sounds like a Next.js app. nice \u{1FAE1}\n\nwhere's the code right now? you can paste a GitHub link, or tell me if it's still on your laptop.",
    chips: ["github.com/me/my-app", "it's only on my laptop", "I'll connect GitHub"]
  },
  {
    text: "perfect. last thing — what URL would you like?\n\nyou can use a custom domain, or grab a free one like `yourapp.dollardeploy.app`.",
    chips: ["myapp.dollardeploy.app", "I have my own domain", "pick one for me"]
  },
  {
    text: "love it. here's the plan:\n\n• small server ($4/mo) in EU\n• we build your Next.js app from `main`\n• HTTPS auto-configured\n• every git push redeploys\n\nready to make it live?",
    cta: true
  }
];

const DEFAULT_GITHUB_REPLY: ChatMessage = {
  role: "ai",
  text: "perfect — GitHub is the fastest path. we'll hook up your repo and auto-deploy on every push.",
  githubCta: true
};

const DEFAULT_CTA_ACTIONS: ChatConfig["ctaActions"] = [
  { label: "Deploy now →", href: "/app/create", variant: "primary" },
  { label: "Browse templates", href: "/r", variant: "secondary" }
];

const DEFAULT_INITIAL_CHIPS: ChatConfig["initialChips"] = [
  { label: "Deploy my Next.js app", icon: "▲" },
  { label: "I have no idea what I'm doing", icon: "❤" },
  { label: "Move my app off localhost", icon: "→" },
  { label: "Just give me a real URL", icon: "✦" }
];

const DEFAULT_PLACEHOLDER =
  'Tell us what you built. e.g. "I made a Next.js app for tracking my plants…"';
const DEFAULT_REPLY_PLACEHOLDER = "Reply…";
const DEFAULT_TRACK_PREFIX = "Chat";
const DEFAULT_GITHUB_MESSAGE = "I'll connect GitHub";

const TypingDots = () => (
  <div className="flex items-start gap-3">
    <div
      className="w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5"
      aria-hidden="true"
    >
      <Icon className="h-3.5 w-3.5 invert-100" />
    </div>
    <div
      className="bg-card border rounded-[18px] rounded-bl-md px-4 py-3.5 shadow-sm inline-flex items-center gap-1"
      aria-label="AI is typing"
    >
      {[0, 150, 300].map(delay => (
        <span
          key={delay}
          className="w-1.5 h-1.5 rounded-full bg-muted-foreground/45 animate-bounce"
          style={{ animationDelay: `${delay}ms`, animationDuration: "1.4s" }}
        />
      ))}
    </div>
  </div>
);

const GitHubDeployCard = ({ ctaActions }: { ctaActions: ChatConfig["ctaActions"] }) => {
  const [open, setOpen] = useState(false);
  const primary = ctaActions.find(a => a.variant === "primary") ?? ctaActions[0];

  return (
    <div className="mt-3 border rounded-lg overflow-hidden shadow-sm bg-card">
      <button
        type="button"
        className="flex items-center gap-2.5 w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-controls="gh-deploy-panel"
      >
        <GitHubIcon size={18} />
        <span className="flex-1 text-sm font-semibold">Connect GitHub & Deploy</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div id="gh-deploy-panel" className="px-4 pb-4 border-t">
          <p className="text-sm text-muted-foreground mt-3 mb-3.5 leading-relaxed">
            We&apos;ll link your repo and auto-redeploy on every{" "}
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">git push</code>. Takes
            about 2 minutes.
          </p>

          {primary && (
            <Button asChild size="sm">
              <Link href={primary.href}>{primary.label}</Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const MessageBubble = ({
  m,
  onChip,
  ctaActions
}: {
  m: ChatMessage;
  onChip: (text: string) => void;
  ctaActions: ChatConfig["ctaActions"];
}) => {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground px-4 py-3 rounded-[18px] rounded-br-md max-w-[78%] text-[15px] leading-normal">
          {m.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div
        className="w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5"
        aria-hidden="true"
      >
        <Icon className="h-3.5 w-3.5 invert-100" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-card border rounded-[18px] rounded-bl-md px-4 py-3 shadow-sm text-md leading-normal markdown prose dark:prose-invert">
          {formatMarkdown(m.text)}
        </div>
        {m.githubCta && <GitHubDeployCard ctaActions={ctaActions} />}
        {m.chips && (
          <div className="flex gap-2 mt-2.5 flex-wrap">
            {m.chips.map(c => (
              <button
                key={c}
                className="bg-card border rounded-full px-3 py-1.5 text-[12.5px] font-medium hover:bg-primary/5 hover:border-primary hover:text-primary transition-colors"
                onClick={() => onChip(c)}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        {m.cta && (
          <div className="flex gap-2.5 mt-3.5 flex-wrap">
            {ctaActions.map(action => (
              <Button
                key={action.href}
                asChild
                variant={action.variant === "primary" ? "default" : "outline"}
                size="sm"
              >
                <Link href={action.href}>{action.label}</Link>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const ChatWidget = ({
  replies,
  githubReply,
  ctaActions,
  initialChips,
  placeholder,
  replyPlaceholder,
  trackPrefix,
  githubMessage,
  className,
  chat: externalChat
}: Partial<ChatConfig> & {
  className?: string;
  chat?: ChatState;
}) => {
  const config: ChatConfig = {
    replies: replies ?? DEFAULT_REPLIES,
    githubReply: githubReply ?? DEFAULT_GITHUB_REPLY,
    ctaActions: ctaActions ?? DEFAULT_CTA_ACTIONS,
    initialChips: initialChips ?? DEFAULT_INITIAL_CHIPS,
    placeholder: placeholder ?? DEFAULT_PLACEHOLDER,
    replyPlaceholder: replyPlaceholder ?? DEFAULT_REPLY_PLACEHOLDER,
    trackPrefix: trackPrefix ?? DEFAULT_TRACK_PREFIX,
    githubMessage: githubMessage ?? DEFAULT_GITHUB_MESSAGE
  };

  const internalChat = useChatFlow(config);
  const { messages, input, typing, send, setInput, reset } = externalChat ?? internalChat;

  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "smooth",
    resize: "smooth"
  });

  const handleSend = (text: string, source: "chip" | "github" | "input") => {
    send(text, source);
    scrollToBottom();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend(input, "input");
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input, "input");
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const target = e.target;
    target.style.height = "auto";
    target.style.height = Math.min(target.scrollHeight, 180) + "px";
  };

  return (
    <div className={cn("w-full max-w-[740px] mx-auto text-left", className)}>
      {messages.length > 0 && (
        <div
          ref={scrollRef}
          className="max-h-[380px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
        >
          <div ref={contentRef} className="flex flex-col gap-4 py-3 px-1">
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                m={m}
                onChip={text => handleSend(text, "chip")}
                ctaActions={config.ctaActions}
              />
            ))}
            {typing && <TypingDots />}
          </div>
        </div>
      )}

      <Card className="p-3.5 shadow-md transition-[border-color,box-shadow] focus-within:border-primary/50 focus-within:ring-4 focus-within:ring-primary/10">
        <form onSubmit={handleSubmit}>
          <textarea
            rows={2}
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleTextareaKeyDown}
            placeholder={messages.length === 0 ? config.placeholder : config.replyPlaceholder}
            className="w-full border-none outline-none resize-none bg-transparent text-[15.5px] leading-normal min-h-[26px] max-h-[180px] py-1 placeholder:text-muted-foreground/40 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Describe your app"
          />
          <div className="flex justify-between items-center mt-2.5 gap-2">
            <div className="flex gap-1.5 items-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={() => handleSend(config.githubMessage, "github")}
                aria-label={config.githubMessage}
              >
                <GitHubIcon size={14} />
                GitHub
              </Button>
              {messages.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => reset()}
                  aria-label="Reset chat"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="flex gap-1.5 items-center">
              <span className="inline-flex items-center gap-1 text-xs text-primary font-mono font-medium bg-primary/8 px-2.5 py-1 rounded-full">
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="6" />
                </svg>
                $deploy
              </span>
              <Button
                type="submit"
                size="icon"
                className="h-9 w-9 rounded-full flex items-center justify-center"
                disabled={!input.trim()}
                aria-label="Send message"
              >
                <Send className="h-4 w-4 mr-0.5 rotate-45" strokeWidth={1} />
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {messages.length === 0 && (
        <div
          className="flex gap-2 flex-wrap justify-center mt-3"
          role="group"
          aria-label="Quick start options"
        >
          {config.initialChips.map(c => (
            <button
              key={c.label}
              className="flex items-center gap-2 bg-accent border rounded-full px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-card hover:border-primary hover:text-primary transition-colors"
              onClick={() => handleSend(c.label, "chip")}
            >
              <span aria-hidden="true">{c.icon}</span>
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
